/**
 * Process Item Drawer Component
 * 
 * Complete drawer solution for adding/editing processes including:
 * - Form component with process details
 * - Search mode for selecting process owner
 * - Automatic process code assignment
 * - Form validation
 * 
 * File Upload Flow:
 * 1. User selects files → Stored as File objects in local state (no upload yet)
 * 2. When form is submitted:
 *    - ADD MODE: Upload all selected files to get fileIds
 *    - EDIT MODE: 
 *      - Compare current files with initial files
 *      - Upload new files to get fileIds
 *      - Delete removed files from backend
 *      - Combine fileIds (existing + new)
 * 3. Pass documentIds and testSheetId to onSubmit
 * 4. This prevents:
 *    - Unnecessary uploads if user cancels
 *    - Data loss from premature deletions
 */

import React, { useState, useEffect } from 'react';
import { Form, Input, Button, Select, Checkbox} from 'antd';
import styled from 'styled-components';
import { SearchOutlined, RightOutlined, LoadingOutlined} from '@ant-design/icons';
import RightContentDrawer from '../../../components/common/RightContentDrawer';
import CloseCircleIcon from '../../../assets/icons/ic-close-circle.svg?react';
import {
    Company,
    getAllCompanies
} from '../../../services/companyService';

import ButtonGroup from '../../../components/common/ButtonGroup';

// ========== TYPE DEFINITIONS ==========

export interface UserFormData {
    id?: string; // Optional id for edit mode
    firstName: string; // Auto-generated for add mode, read-only for edit mode
    lastName: string;
    phoneNumber: string | null;
    jobTitle?: string | null;
    department?: string | null;
    location?: string | null;
    company?: Company | null;
    companyName?: string | null;
    userType: string;
    email: string;
    companies?: Company[] | null;
    isPrimaryCompanyUser: boolean;
}

export interface ProcessItemDrawerProps {
    open: boolean;
    mode: 'add' | 'edit';
    initialValues?: Partial<UserFormData>;
    nextProcessCode?: string; // For add mode: the next process code (A01, A02...)
    autoEnterSearchMode?: boolean; // Auto enter search mode on open (for missing owner)
    onClose: () => void;
    onSubmit: (values: UserFormData) => Promise<void>;
}

// ========== STYLED COMPONENTS ==========

const FormLabel = styled.div`
  font-family: Inter;
  font-weight: 600;
  font-size: 16px;
  line-height: 100%;
  letter-spacing: 0.005em;
  color: #1F2937;
  margin-bottom: 10px;
  display: flex;
  align-items: center;
  gap: 6px;

  .info-icon {
    color: #9CA3AF;
    font-size: 14px;
    cursor: help;

    &:hover {
      color: #6B7280;
    }
  }
`;

const StyledForm = styled(Form)`
  display: flex;
  flex-direction: column;
  gap: 20px;

  .ant-form-item {
    margin-bottom: 0 !important;
  }
` as typeof Form;

const StyledInput = styled(Input)`
  font-family: Inter;
  font-weight: 500;
  font-size: 14px;
  line-height: 100%;
  border-radius: 8px !important;
  letter-spacing: 0.005em;
  color: #6B7280;
  height: 44px;

  &.ant-input {
    color: #6B7280;
  }

  &::placeholder {
    color: #9CA3AF;
  }

  &:disabled {
    background: #F9FAFB;
    color: #9CA3AF;
    cursor: not-allowed;
  }
`;

const StyledSelect = styled(Select)`
  width: 100%;
  height: 44px;

  .ant-select-selector {
    height: 44px !important;
    border-radius: 8px !important;
    font-family: Inter;
    font-weight: 500;
    font-size: 14px;
    letter-spacing: 0.005em;
    color: #6B7280;
    display: flex;
    align-items: center;

    &::placeholder {
      color: #9CA3AF;
    }
  }

  .ant-select-selection-item {
    line-height: 42px !important;
  }

  &.ant-select-disabled .ant-select-selector {
    background: #F9FAFB;
    color: #9CA3AF;
    cursor: not-allowed;
  }
`;

const InfoText = styled.div`
  font-family: Inter;
  font-weight: 400;
  font-size: 13px;
  line-height: 150%;
  color: #6B7280;
  margin-top: 6px;
`;

const CharacterCounter = styled.div<{ $isNearLimit?: boolean }>`
  font-family: Inter;
  font-weight: 400;
  font-size: 12px;
  line-height: 150%;
  color: ${props => props.$isNearLimit ? '#EF4444' : '#9CA3AF'};
  margin-top: 4px;
  text-align: right;
`;

// Search UI Components
const SearchContainer = styled.div`
  display: flex;
  flex-direction: column;
  height: 100%;
`;

const SearchInputWrapper = styled.div`
  margin-bottom: 24px;

  .ant-input-affix-wrapper {
    height: 44px;
    border-radius: 6px;
    font-family: Inter;
    font-weight: 500;
    font-size: 14px;

    .ant-input {
      font-family: Inter;
      font-weight: 500;
      font-size: 14px;
      color: #6B7280;
    }

    .anticon-search {
      color: #6B7280;
    }
  }
`;

const SearchResultsList = styled.div`
  flex: 1;
  overflow-y: auto;
`;

const SearchResultItem = styled.div<{ $selected: boolean }>`
  padding: 12px 16px;
  border-radius: 6px;
  margin-bottom: 8px;
  cursor: pointer;
  transition: all 0.2s;
  background: ${props => props.$selected ? '#EFF6FF' : 'transparent'};
  border: 1px solid ${props => props.$selected ? '#2563EB' : '#E5E7EB'};

  &:hover {
    background: ${props => props.$selected ? '#EFF6FF' : '#F3F4F6'};
  }

  .item-label {
    font-family: Inter;
    font-weight: 500;
    font-size: 14px;
    line-height: 20px;
    color: ${props => props.$selected ? '#2563EB' : '#1F2937'};
  }

  .item-sublabel {
    font-family: Inter;
    font-weight: 400;
    font-size: 12px;
    line-height: 16px;
    color: #6B7280;
    margin-top: 4px;
  }
`;

const TagContainer = styled.div`
  position: relative;
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 8px;
  padding: 8px 32px 8px 12px;
  min-height: 44px;
  border: 1px solid #D1D5DB;
  border-radius: 8px;
  background: white;
  cursor: pointer;
  outline: none;
  transition: border-color 0.2s, box-shadow 0.2s;

//   &:hover {
//     border-color: #2563EB;
//   }

//   &:focus {
//     border-color: #2563EB;
//     box-shadow: 0 0 0 2px rgba(37, 99, 235, 0.2);
//   }
`;

const ArrowButton = styled.div`
  position: absolute;
  top: 50%;
  right: 4px;
  transform: translateY(-50%);
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  padding: 8px;
  
  .anticon {
    color: #6B7280;
    font-size: 12px;
    transition: color 0.2s;
  }

  &:hover .anticon {
    color: #2563EB;
  }
`;

const EmptyState = styled.div`
  text-align: center;
  padding: 40px 20px;
  color: #9CA3AF;
  font-family: Inter;
  font-size: 14px;
`;

const Tag = styled.span<{
    $backgroundColor: string;
    $color: string;
}>`
  display: inline-flex;
  align-items: center;
  gap: 4px;
  padding: 4px 8px;
  background: ${props => props.$backgroundColor};
  border: none;
  border-radius: 50px;
  font-family: Inter;
  font-weight: 500;
  font-size: 14px;
  color: ${props => props.$color};
  cursor: default;
  transition: all 0.2s;
`;

const StyledCloseIcon = styled(CloseCircleIcon) <{ $circleFill: string; $strokeColor: string }>`
  cursor: pointer;
  flex-shrink: 0;
  
  path:first-child {
    fill: ${props => props.$circleFill};
  }
  
  path:last-child {
    stroke: ${props => props.$strokeColor};
  }
`;

// ========== MAIN COMPONENT ==========

const InviteUserDrawer: React.FC<ProcessItemDrawerProps> = ({
    open,
    mode,
    initialValues,
    nextProcessCode,
    autoEnterSearchMode = false,
    onClose,
    onSubmit,
}) => {
    // Form states
    const [form] = Form.useForm();
    const [email, setEmail] = useState('');
    const [firstName, setFirstName] = useState('');
    const [lastName, setLastName] = useState('');
    const [phoneNumber, setPhoneNumber] =  useState('');
    const [jobTitle, setJobTitle] =  useState('');
    const [department, setDepartment] =  useState('');
    const [userType, setUserType] = useState('');

    const [isPrimaryCompanyUser, setIsPrimaryCompanyUser] = useState(false);

    const [company, setCompany] = useState<Company | null>(null);
    const [companyName, setCompanyName] = useState('');

    const [companies, setCompanies] = useState<Company[] | null>(null);
    
    // UI states
    const [loading, setLoading] = useState(false);
    const [isFormValid, setIsFormValid] = useState(false);
    const [isSearchMode, setIsSearchMode] = useState(false);
    const [searchQuery, setSearchQuery] = useState('');
    const [tempSelectedCompany, setTempSelectedCompany] = useState<Company | null>(null);
    const [tempSelectedCompanies, setTempSelectedCompanies] = useState<Company[] | null>(null);

    // Search results states
    const [companyResults, setCompanyResults] = useState<Company[]>([]);
    const [isSearching, setIsSearching] = useState(false);

    // Reset form when initialValues change
    useEffect(() => {
        
        // Add mode - reset to empty
        if (mode === 'edit' && initialValues) {
                    // Edit mode - load existing values
                    setUserType(initialValues.userType || '');
                    setEmail(initialValues.email || '');
                    setFirstName(initialValues.firstName || '');
                    setLastName(initialValues.lastName || '');
                    setPhoneNumber(initialValues.phoneNumber || '');
                    setCompanyName(initialValues.companyName || '')
                    setJobTitle(initialValues.jobTitle || '');
                    setDepartment(initialValues.department || '');
                    setCompanies(initialValues.companies || null);
                    setIsPrimaryCompanyUser(initialValues.isPrimaryCompanyUser || false);
                    
        
                    form.setFieldsValue({
                        userType: initialValues.userType,
                        email: initialValues.email,
                        firstName: initialValues.firstName,
                        lastName: initialValues.lastName,
                        phoneNumber: initialValues.phoneNumber,
                        companyName: initialValues.companyName,
                        jobTitle: initialValues.jobTitle,
                        department: initialValues.department,
                        companies: initialValues.companies,
                        isPrimaryCompanyUser: initialValues.isPrimaryCompanyUser
                    });
        
              
                } else {
                    // Add mode - reset to empty
                    resetForm();
                }
        
    }, [open, mode, initialValues, nextProcessCode, autoEnterSearchMode]);

    useEffect(() => {
        if (open && autoEnterSearchMode) {
            setTimeout(() => {
                setIsSearchMode(true);
                if (userType == "internal-admin" || userType == "process-owner"){
                    setTempSelectedCompany(initialValues?.company || null);
                }
                else {
                    setTempSelectedCompanies(initialValues?.companies || null);
                }
                
            }, 200);
        }
    }, [open, autoEnterSearchMode]);

    // Watch for field changes and validate
    useEffect(() => {
        const checkFormValidation = () => {
            const hasName = !!firstName && firstName.trim() !== '' && !!lastName && lastName.trim() !== '';
            const hasEmail = !!email && email.trim() !== '';
            const companyIsRequired = userType != "process-owner" || company != null;

            const isValid = hasName && hasEmail && companyIsRequired;
            setIsFormValid(isValid);
        };

        checkFormValidation();
    }, [firstName, lastName, email, userType, company]);

    const resetForm = () => {
        setEmail('');
        setFirstName('');
        setLastName('');
        setPhoneNumber('');
        setJobTitle('');
        setDepartment('');
        setCompany(null);
        setUserType('');
        setCompanyName('');
        setCompanies(null);
        form.resetFields();
    };

    // Search mode handlers
    const enterSearchMode = () => {
        setIsSearchMode(true);
        setSearchQuery('');
        if (userType == "internal-admin" || userType == "process-owner"){
            setTempSelectedCompany(company);
        }
        else {
            setTempSelectedCompanies([...companies ?? []]);
        }
        
        // Clear previous results
        setCompanyResults([]);
    };

    const exitSearchMode = () => {
        setIsSearchMode(false);
        setSearchQuery('');
        setTempSelectedCompany(null);
        setTempSelectedCompanies(null);
        setCompanyResults([]);
    };

    // Fetch search results when query changes
    useEffect(() => {
        const fetchSearchResults = async () => {
            if (!isSearchMode) return;

            

            setIsSearching(true);

            try {
                const results = await getAllCompanies({
                    companyName: searchQuery ?? ""
                });
                setCompanyResults(results.data);
            } catch (error) {
                console.error('Search failed:', error);
                setCompanyResults([]);
            } finally {
                setIsSearching(false);
            }
        };

        // Debounce search (wait 300ms after user stops typing)
        const timeoutId = setTimeout(fetchSearchResults, 300);
        return () => clearTimeout(timeoutId);
    }, [searchQuery, isSearchMode]);

    const handleSearchResultClick = (company: Company) => {
        if (userType == "internal-admin" || userType == "process-owner"){
            // Toggle selection
            if (tempSelectedCompany?.companyId === company.companyId) {
                setTempSelectedCompany(null);
                //setCompanyName(null);
            } else {
                setTempSelectedCompany(company);
                setCompanyName(company.companyName);
            }
        }
        else {
            if (tempSelectedCompanies?.map(c => c.companyId).includes(company.companyId)) {
                setTempSelectedCompanies(tempSelectedCompanies.filter(c => c.companyId != company.companyId));
                //setTempSelectedCompany(null);
                //setCompanyName(null);
            } else {
                let newCompanyList = [...tempSelectedCompanies ?? [], company];
                setTempSelectedCompanies(newCompanyList);
            }
        }
    };

    const applySearchSelection = () => {
        if (userType == "internal-admin" || userType == "process-owner"){
            setCompany(tempSelectedCompany);
            form.setFieldValue('company', tempSelectedCompany);
            setCompanyName(tempSelectedCompany?.companyName ?? '');
            form.setFieldValue('companyName', tempSelectedCompany?.companyName);
        }
        else {
            setCompanies(tempSelectedCompanies);
            form.setFieldValue('companies', tempSelectedCompanies);
        }
        
        exitSearchMode();
    };

   

    const handleClose = () => {
        setIsFormValid(false);
        setIsSearchMode(false);
        resetForm();
        onClose();
    };

    // Handle drawer close button (X) - context-aware
    const handleDrawerClose = () => {
        if (isSearchMode) {
            exitSearchMode();
        } else {
            handleClose();
        }
    };

    const handleFormSubmit = async () => {
        setLoading(true);
        try {
            


            // Submit form with file IDs
            await onSubmit({
                id: mode === 'edit' ? initialValues?.id : undefined,
                firstName: firstName,
                lastName: lastName,
                phoneNumber: phoneNumber,
                jobTitle: jobTitle,
                department: department,
                company: company,
                userType: userType,
                email: email,
                companyName: companyName,
                companies: companies,
                isPrimaryCompanyUser: isPrimaryCompanyUser
            });

            handleClose();
        } catch (error) {
            console.error("Form submission failed:", error);
        } finally {
            setLoading(false);
        }
    };

    // Render selected process owner tag
    const renderCompanyTag = (company: Company | null) => {
        if (!company?.companyName && !companies) {
            return (
                <span style={{
                    fontFamily: 'Inter',
                    fontWeight: 500,
                    fontSize: '14px',
                    color: '#9CA3AF'
                }}>
                    Search Company
                </span>
            );
        }

        return (
            <Tag
                $backgroundColor="#F8FAFC"
                $color="#1F2937"
            >
                {company?.companyName}
                {mode === 'add' || (userType == 'ExternalAdministrators' || userType == 'ExternalUsers') ? <StyledCloseIcon
                    $circleFill="#E5E7EB"
                    $strokeColor="#6B7280"
                    onClick={(e) => {
                        e.stopPropagation();
                        setCompany(null);
                        form.setFieldValue('company', null);
                        setCompanies(companies?.filter(c => c != company) ?? null);
                        form.setFieldValue('companies', companies?.filter(c => c != company) ?? null);
                    }}
                /> : 
                ""}
            </Tag>
        );
    };

    // Render search UI
    const renderSearchUI = () => {
        return (
            <SearchContainer>
                <SearchInputWrapper>
                    <Input
                        prefix={<SearchOutlined />}
                        suffix={
                            isSearching && searchQuery !== '' ? (
                                <LoadingOutlined style={{ color: '#2563EB' }} />
                            ) : null
                        }
                        placeholder="Search Company..."
                        value={searchQuery}
                        onChange={(e) => setSearchQuery(e.target.value)}
                        autoFocus
                    />
                </SearchInputWrapper>

                <SearchResultsList>
                    {companyResults.length === 0 ? (
                        searchQuery === '' ? (
                            <EmptyState>{isSearching ? 'Loading...' : 'There are no companies to display'}</EmptyState>
                        ) : (
                            <EmptyState>{isSearching ? 'Searching...' : 'There are no results found'}</EmptyState>
                        )
                    ) : (
                        companyResults.map((company) => {
                            let isSelected = tempSelectedCompany?.companyId === company.companyId;

                            if (userType == 'ExternalAdministrators' || userType == 'ExternalUsers' || userType == 'external-admin' || userType == 'external-user') {
                                isSelected = tempSelectedCompanies?.map(c => c.companyId).includes(company.companyId) ?? false;
                            }

                            return (
                                <SearchResultItem
                                    key={company.companyId}
                                    $selected={isSelected}
                                    onClick={() => handleSearchResultClick(company)}
                                >
                                    <div className="item-label">{company.companyName}</div>
                                    {/* <div className="item-sublabel">{owner.title}</div> */}
                                </SearchResultItem>
                            );
                        })
                    )}
                </SearchResultsList>
            </SearchContainer>
        );
    };

    // Render form UI
    const renderFormUI = () => (
        <StyledForm
            id="user-form"
            form={form}
            layout="vertical"
            requiredMark={false}
        >
            {/* User Type */}
            <Form.Item name="userType">
                <div>
                    <FormLabel>User Type</FormLabel>
                    <StyledSelect
                        value={userType}
                        onChange={(value) => setUserType(value as string)}
                        disabled={mode === 'add' ? false : true}
                        options={ mode === 'add' ? [
                            { label: 'External Admin', value: 'external-admin' },
                            { label: 'Internal Admin', value: 'internal-admin' },
                            { label: 'Process Owner', value: 'process-owner' },
                            { label: 'External User', value: 'external-user' },
                        ] : [
                            { label: 'External Admin', value: 'externalAdministrators' },
                            { label: 'Internal Admin', value: 'internalAdministrators' },
                            { label: 'Process Owner', value: 'processOwners' },
                            { label: 'External User', value: 'externalUser' },
                        ]
                        }
                    />
                    <InfoText>
                        User Type is read-only after initial user creation
                    </InfoText>
                </div>
            </Form.Item>

            {/* Email */}
            <Form.Item
                name="email"
                rules={[
                    { required: true, message: 'Please enter an email' },
                    { whitespace: true, message: 'Email cannot be empty' },
                ]}
            >
                <div>
                    <FormLabel>Email</FormLabel>
                    <StyledInput
                        value={email}
                        onChange={(e) => {
                            const value = e.target.value;
                            // Enforce maxLength of 100 characters
                            if (value.length <= 100) {
                                setEmail(value);
                            }
                        }}
                        placeholder="Enter email"
                        maxLength={100}
                        spellCheck={true}
                        disabled={mode === 'add' ? false : true}
                    />
                    <CharacterCounter $isNearLimit={email.length >= 90}>
                        {email.length}/100
                    </CharacterCounter>
                </div>
            </Form.Item>


            {/* First Name */}
            <Form.Item
                name="firstName"
                rules={[
                    { required: true, message: 'Please enter a first name' },
                    { whitespace: true, message: 'First name cannot be empty' },
                ]}
            >
                <div>
                    <FormLabel>First Name</FormLabel>
                    <StyledInput
                        value={firstName}
                        onChange={(e) => {
                            const value = e.target.value;
                            // Enforce maxLength of 100 characters
                            if (value.length <= 100) {
                                setFirstName(value);
                            }
                        }}
                        placeholder="Enter first name"
                        maxLength={100}
                        spellCheck={true}
                    />
                    <CharacterCounter $isNearLimit={firstName.length >= 90}>
                        {firstName.length}/100
                    </CharacterCounter>
                </div>
            </Form.Item>

            {/* Last Name */}
            <Form.Item
                name="lastName"
                rules={[
                    { required: true, message: 'Please enter a last name' },
                    { whitespace: true, message: 'Last name cannot be empty' },
                ]}
            >
                <div>
                    <FormLabel>Last Name</FormLabel>
                    <StyledInput
                        value={lastName}
                        onChange={(e) => {
                            const value = e.target.value;
                            // Enforce maxLength of 100 characters
                            if (value.length <= 100) {
                                setLastName(value);
                            }
                        }}
                        placeholder="Enter last name"
                        maxLength={100}
                        spellCheck={true}
                    />
                    <CharacterCounter $isNearLimit={lastName.length >= 90}>
                        {lastName.length}/100
                    </CharacterCounter>
                </div>
            </Form.Item>

            {/* Phone Number */}
            <Form.Item
                name="phoneNumber"
            >
                <div>
                    <FormLabel>Phone Number</FormLabel>
                    <StyledInput
                        value={phoneNumber}
                        onChange={(e) => {
                            const value = e.target.value;
                            // Enforce maxLength of 13 characters
                            if (value.length <= 13) {
                                setPhoneNumber(value);
                            }
                        }}
                        placeholder="Enter phone number (optional)"
                        maxLength={13}
                        spellCheck={true}
                    />
                    <CharacterCounter $isNearLimit={phoneNumber.length >= 11}>
                        {phoneNumber.length}/13
                    </CharacterCounter>
                </div>
            </Form.Item>

            <Form.Item name="company">

                {mode === 'add' && (userType == 'internal-admin') ?
                <div>
                    <FormLabel>Company</FormLabel>
                    <TagContainer
                        style={{cursor:mode === 'add' ? "pointer" : "not-allowed"}}
                        tabIndex={0}
                        onClick={() => {
                            if (mode === 'add') {
                                enterSearchMode();
                            }
                        }}
                        onFocus={() => {
                            // Auto enter search mode when focused via Tab
                            if (mode === 'add') {
                                enterSearchMode();
                            }
                        }}
                        onKeyDown={(e) => {
                            // Enter search mode on Enter or Space key
                            if (e.key === 'Enter' || e.key === ' ') {
                                e.preventDefault();
                                if (mode === 'add') {
                                enterSearchMode();
                            }
                            }
                        }}
                        
                    >
                        {renderCompanyTag(company)}
                        {mode === 'add' ? <ArrowButton
                            onClick={(e) => {
                                e.stopPropagation();
                                enterSearchMode();
                            }}
                        >
                            <RightOutlined />
                        </ArrowButton> : ""}
                    </TagContainer>
                    <InfoText>
                        Company assignment is read-only after initial user creation. Leave blank if internal admin is for a new company
                    </InfoText>
                </div> : ""}

                {mode === 'add' && (userType == 'process-owner') ?
                <div>
                    <FormLabel>Company</FormLabel>
                    <TagContainer
                        style={{cursor:mode === 'add' ? "pointer" : "not-allowed"}}
                        tabIndex={0}
                        aria-required={true}
                        onClick={() => {
                            if (mode === 'add') {
                                enterSearchMode();
                            }
                        }}
                        onFocus={() => {
                            // Auto enter search mode when focused via Tab
                            if (mode === 'add') {
                                enterSearchMode();
                            }
                        }}
                        onKeyDown={(e) => {
                            // Enter search mode on Enter or Space key
                            if (e.key === 'Enter' || e.key === ' ') {
                                e.preventDefault();
                                if (mode === 'add') {
                                enterSearchMode();
                            }
                            }
                        }}
                        
                    >
                        {renderCompanyTag(company)}
                        {mode === 'add' ? <ArrowButton
                            onClick={(e) => {
                                e.stopPropagation();
                                enterSearchMode();
                            }}
                        >
                            <RightOutlined />
                        </ArrowButton> : ""}
                    </TagContainer>
                    <InfoText>
                        Company assignment is required for process owners
                    </InfoText>
                </div> : ""}

                {mode === 'edit' && (userType == 'InternalAdministrators' || userType == 'ProcessOwners') ?
                <div>
                    <FormLabel>Company Name</FormLabel>
                    <StyledInput
                        value={companyName}
                        onChange={(e) => {
                            const value = e.target.value;
                            // Enforce maxLength of 100 characters
                            if (value.length <= 100) {
                                setCompanyName(value);
                            }
                        }}
                        placeholder="Enter company name"
                        maxLength={100}
                        spellCheck={true}
                    />
                    <CharacterCounter $isNearLimit={companyName.length >= 90}>
                        {companyName.length}/100
                    </CharacterCounter>
                </div> : ""}



                {(userType == 'ExternalAdministrators' || userType == 'ExternalUsers' || userType == 'external-admin' || userType == 'external-user') ?
                <div>
                    <FormLabel>Companies</FormLabel>
                    <TagContainer
                        tabIndex={0}
                        onClick={() => {
                                enterSearchMode();
                            
                        }}
                        onFocus={() => {
                            // Auto enter search mode when focused via Tab
                            enterSearchMode();
                            
                        }}
                        onKeyDown={(e) => {
                            // Enter search mode on Enter or Space key
                            if (e.key === 'Enter' || e.key === ' ') {
                                e.preventDefault();

                                enterSearchMode();
                            }
                        }}
                        
                    >
                        {companies?.map((item) => 
                        <div>
                            {renderCompanyTag(item)}
                        <ArrowButton
                            onClick={(e) => {
                                e.stopPropagation();
                                enterSearchMode();
                            }}
                        >
                            <RightOutlined />
                        </ArrowButton>
                        </div>) }
                    </TagContainer>
                    <InfoText>
                        Users will have access to all chosen companies' records
                    </InfoText>
                </div> : ""}
            </Form.Item>

            {userType == "InternalAdministrators" || userType == "internal-admins" ? 
            <Form.Item>
                    
                    <div style={{ display: 'flex'}}>
                    <Checkbox
                        checked={isPrimaryCompanyUser}
                        onChange={() => setIsPrimaryCompanyUser(!isPrimaryCompanyUser)}
                    />
                    <FormLabel style={{paddingLeft: 15, marginBottom: 0 }}>Is Primary Company User</FormLabel>
                    </div>
                    <InfoText>
                        Only one Internal Admin can be the primary user. Setting a user to this designation will remove others from primary role
                    </InfoText>

            </Form.Item> : ""}

            
            {/* Job Title */}
            {userType == "ProcessOwners" || userType == "process-owner" && 
            <Form.Item
                name="jobTitle"
                // rules={[
                //     { required: false, message: 'Please enter a job title' },
                //     { whitespace: false, message: 'Job title cannot be empty' },
                // ]}
            >
                <div>
                    <FormLabel>Job Title</FormLabel>
                    <StyledInput
                        value={jobTitle}
                        onChange={(e) => {
                            const value = e.target.value;
                            // Enforce maxLength of 100 characters
                            if (value.length <= 100) {
                                setJobTitle(value);
                            }
                        }}
                        placeholder="Enter job title"
                        maxLength={100}
                        spellCheck={true}
                    />
                    <CharacterCounter $isNearLimit={jobTitle.length >= 90}>
                        {jobTitle.length}/100
                    </CharacterCounter>
                </div>
            </Form.Item>
            }
            {/* Department */}
            {(userType == "ProcessOwners" || userType == "process-owner") && 
            <Form.Item
                name="department"
                // rules={[
                //     { required: false, message: 'Please enter a job title' },
                //     { whitespace: false, message: 'Job title cannot be empty' },
                // ]}
            >
                <div>
                    <FormLabel>Department</FormLabel>
                    <StyledInput
                        value={department}
                        onChange={(e) => {
                            const value = e.target.value;
                            // Enforce maxLength of 100 characters
                            if (value.length <= 100) {
                                setDepartment(value);
                            }
                        }}
                        placeholder="Enter department"
                        maxLength={100}
                        spellCheck={true}
                    />
                    <CharacterCounter $isNearLimit={department.length >= 90}>
                        {department.length}/100
                    </CharacterCounter>
                </div>
            </Form.Item>
            }
        </StyledForm>
    );

    const drawerTitle = mode === 'add' ? 'Invite User' : 'Edit User';

    return (
        <RightContentDrawer
            open={open}
            title={drawerTitle}
            width={572}
            onClose={handleDrawerClose}
            footer={
                isSearchMode ? (
                    // Search mode footer: Go back + Done buttons
                    <ButtonGroup>
                        <Button type="default" onClick={exitSearchMode}>
                            Go back
                        </Button>
                        <Button type="primary" onClick={applySearchSelection}>
                            Done
                        </Button>
                    </ButtonGroup>
                ) : (
                    // Normal mode footer: Go back + Submit buttons
                    <ButtonGroup>
                        <Button type="default" onClick={handleClose} disabled={loading}>
                            Go back
                        </Button>
                        <Button
                            type="primary"
                            loading={loading}
                            disabled={!isFormValid}
                            form="user-form"
                            onClick={handleFormSubmit}
                        >
                            {mode === 'add' ? 'Invite User' : 'Save changes'}
                        </Button>
                    </ButtonGroup>
                )
            }
        >
            {isSearchMode ? renderSearchUI() : renderFormUI()}
        </RightContentDrawer>
    );
};

export default InviteUserDrawer;

